// Berklee College of Music: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Berklee's Common Data Set 2025-2026: C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C21/C22 plans, G1/G5 costs for 2026-2027. Departure: Berklee
//    says its CDS counts include Berklee Online's degree-seeking students
//    and Boston Conservatory at Berklee, while its policy and cost answers
//    are for the Boston campus college. The page says so beside the counts.
//  - college.berklee.edu admissions: Undergraduate Admissions Deadlines (the
//    fall table; the cycle that opened July 1, 2026, for fall 2027 entry) and
//    How to Apply (testing, auditions, the BA programs).
//  - college.berklee.edu, Bachelor of Music: Majors (the majors file).
//  - College Scorecard API, school id 164748 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Berklee Common Data Set 2025-2026',
        url: 'https://www.berklee.edu/sites/default/files/2026-02/CDS_2025-2026.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Berklee Admissions, Undergraduate Admissions Deadlines',
        url: 'https://college.berklee.edu/admissions/undergraduate/deadlines',
        read: '2026-10-01',
    },
    apply: {
        label: 'Berklee Admissions, How to Apply',
        url: 'https://college.berklee.edu/admissions/undergraduate/how-to-apply',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?164748-Berklee-College-of-Music',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'berklee-college-of-music',
    kbId: 'berklee_college_of_music',
    name: 'Berklee College of Music',
    shortName: 'Berklee',
    location: 'Boston, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Berklee web standards style guide); decoration only.
    branding: {
        primary: '#D81118',
        secondary: '#4C5960',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/berklee_college_of_music_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 6671,
        admitted: 3675,
        enrolled: 1466,
        source: 'cds',
        where: 'section C1 (Berklee\'s CDS counts Berklee Online degree students and Boston Conservatory at Berklee with the college)',
        residency: [
            { group: 'In-state', applied: 364, admitted: 183 },
            { group: 'Out-of-state', applied: 4423, admitted: 2416 },
            { group: 'International', applied: 1882, admitted: 1075 },
            { group: 'Unknown', applied: 2, admitted: 1 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Berklee\'s Common Data Set reports one first-year acceptance rate, with no rate by program or major. Bachelor of Music students declare a major after they enroll.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Berklee has no early decision plan. Its early action plan is not binding and not restrictive, and the Common Data Set gives no early action counts, so there is no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Berklee does not take the Common App. You apply to one of three bachelor\'s programs, and each asks for something different:',
        rows: [
            { school: 'Bachelor of Music', requirement: 'A live audition and interview, online or in person. You choose a major after you enroll, by the end of your second semester.' },
            { school: 'BA in Black Music and Culture', requirement: 'An audition and interview. You apply directly to the program.' },
            { school: 'BA in Music Industry Leadership and Innovation', requirement: 'No audition. An online interview, and a portfolio with a resume or CV and a one-page cover letter.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Action', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 31', binding: false },
            { plan: 'Regular Action', deadline: 'January 15', decision: 'By March 31', binding: false },
        ],
        note: 'The application and supporting documents are due on the deadline. Auditions and interviews run August through January for Early Action and August through March for Regular Action. The deposit is due May 1. Pre-college summer program students could apply by July 30, 2026, audition in late July or August 1, and hear by October 15.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'Berklee is test-blind: SAT and ACT scores are not considered and not required. It does not require essays or a personal statement; a personal essay is an optional extra. International applicants whose first language is not English send an English test score.',
        source: 'apply',
        cdsLine: 'The Common Data Set marks the SAT and ACT as not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 54020 },
            { item: 'Required fees', amount: 1600 },
            { item: 'Housing and food (on campus)', amount: 21300 },
            { item: 'Books and supplies', amount: 1260 },
            { item: 'Transportation', amount: 1180 },
            { item: 'Other expenses', amount: 1460 },
        ],
        total: 80820,
        note: 'For a full-time undergraduate living on the Boston campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 47587,
            byIncome: [
                { income: '$0 to $30,000', amount: 42991 },
                { income: '$30,001 to $48,000', amount: 34945 },
                { income: '$48,001 to $75,000', amount: 32977 },
                { income: '$75,001 to $110,000', amount: 45303 },
                { income: 'Over $110,000', amount: 55038 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Berklee\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
